/**
 * Determines the order and visibility of facet filters based on
 * the Algolia dashboard's "Facet Display" configuration.
 *
 * This mirrors the logic used on the search page via InstantSearch's
 * dynamicWidgets, but implemented as a standalone utility so the
 * Vue-based collection filters can use it too.
 */

const KNOWN_ATTRIBUTES = [
  'product_type',
  'tags',
  'options.color',
  'available_sizes',
  'inventory_available',
  'price',
];

const EXCLUDED_FACETS = new Set([
  'inventory_available',
  'collections',
  'id',
  'objectID',
  'handle',
  'title',
  'body_html_safe',
  'image',
  'vendor',
  'variants_count',
  'published_at',
  'created_at',
  'updated_at',
  'options.size',
]);

const ATTRIBUTE_LABELS = {
  product_type: 'Category',
  tags: 'Type',
  'options.color': 'Color',
  'options.size': 'Size',
  available_sizes: 'Size',
  inventory_available: 'Availability',
  price: 'Price',
  'meta.custom.color_group': 'Color',
};

const ATTRIBUTE_TO_FILTER_KEY = {
  'product_type': 'category',
  'meta.custom.color_group': 'color',
  'options.color': 'color',
  'options.size': 'size',
  'available_sizes': 'size',
  'tags': 'type',
  'price': 'price',
};

const COLOR_ATTRIBUTE_FALLBACKS = {
  'meta.custom.color_group': 'options.color',
  'options.color': 'meta.custom.color_group',
  'options.size': 'available_sizes',
  'available_sizes': 'options.size',
};

const CATEGORY_TAG_PATTERN = /^\[Category:\s*(.+?)\]$/;

/**
 * Extracts the facet ordering from an Algolia response's renderingContent.
 * Returns null if no dashboard ordering is configured.
 *
 * @param {Object|null} renderingContent - From the Algolia search response
 * @returns {string[]|null} Ordered array of facet attribute names, or null
 */
export function getDashboardFacetOrder(renderingContent) {
  const facetOrder = renderingContent
    ?.facetOrdering
    ?.facets
    ?.order;

  if (Array.isArray(facetOrder) && facetOrder.length > 0) {
    return facetOrder;
  }

  return null;
}

/**
 * Builds the ordered list of facets to display, with filter keys for
 * mapping to the Vue filter components.
 *
 * For the `tags` attribute, values are filtered to only include
 * [Category: ...] tags, with labels stripped for display.
 *
 * @param {Object} options
 * @param {Object} options.facets - Facet values from the Algolia response
 * @param {Object|null} options.renderingContent - From the Algolia response
 * @returns {Array<{ attribute: string, label: string, filterKey: string, values: Object }>}
 */
export function getOrderedFacets({ facets, renderingContent }) {
  const availableAttributes = Object.keys(facets || {});

  if (!availableAttributes.length) {
    return [];
  }

  const dashboardOrder = getDashboardFacetOrder(renderingContent);
  let orderedAttributes;

  if (dashboardOrder) {
    const availableSet = new Set(availableAttributes);
    orderedAttributes = dashboardOrder
      .map((attr) => {
        if (availableSet.has(attr)) return attr;
        const fallback = COLOR_ATTRIBUTE_FALLBACKS[attr];
        if (fallback && availableSet.has(fallback)) return fallback;
        return null;
      })
      .filter((attr) => attr && !EXCLUDED_FACETS.has(attr));
  } else {
    orderedAttributes = buildFallbackOrder(availableAttributes)
      .filter((attr) => !EXCLUDED_FACETS.has(attr));
  }

  return orderedAttributes.map((attribute) => {
    const filterKey = ATTRIBUTE_TO_FILTER_KEY[attribute] || deriveFilterKey(attribute);
    let values = facets[attribute] || {};

    if (attribute === 'tags') {
      values = extractCategoryTags(values);
    }

    return {
      attribute,
      label: ATTRIBUTE_LABELS[attribute] || humanizeAttribute(attribute),
      filterKey,
      values,
    };
  });
}

/**
 * Filters tag facet values to only [Category: ...] entries
 * and strips the prefix for display.
 *
 * @param {Object} tagValues - { "[Category: Insulated]": 5, "sale": 10, ... }
 * @returns {Object} - { "Insulated": 5 }
 */
function extractCategoryTags(tagValues) {
  const filtered = {};

  for (const [tag, count] of Object.entries(tagValues)) {
    const match = tag.match(CATEGORY_TAG_PATTERN);
    if (match) {
      filtered[match[1].trim()] = count;
    }
  }

  return filtered;
}

/**
 * Derives a clean filter key from an Algolia attribute name.
 * e.g. "named_tags.Fill" → "fill"
 */
function deriveFilterKey(attribute) {
  return attribute
    .split('.')
    .pop()
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Fallback ordering: known attributes first (in their predefined order),
 * then any discovered attributes sorted alphabetically.
 */
function buildFallbackOrder(availableAttributes) {
  const availableSet = new Set(availableAttributes);
  const knownSet = new Set(KNOWN_ATTRIBUTES);

  const knownPresent = KNOWN_ATTRIBUTES.filter((attr) => availableSet.has(attr));
  const discovered = availableAttributes
    .filter((attr) => !knownSet.has(attr))
    .sort();

  return knownPresent.concat(discovered);
}

/**
 * Converts an Algolia attribute name to a human-readable label.
 * e.g. "named_tags.Fill" → "Fill"
 */
function humanizeAttribute(attribute) {
  if (!attribute) {
    return '';
  }

  return attribute
    .split('.')
    .pop()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
